import React, {useEffect, useState} from 'react';
import Navigation from '../components/Navigation';
import {Header, Footer} from '../components/Layout';
import Sidebar from '../components/Sidebar';
import FormField from '../components/FormField';
import FormFieldMultiSelectDropdown from '../components/FormFieldMultiSelectDropdown';
import FormFieldSingleSelectDropdown from '../components/FormFieldSingleSelectDropdown';

import FormSection from '../components/FormSection';
import EmailVerificationField from '../components/EmailVerificationField';
import ContractsSection from './ContractsSection';
import RegistrationSection from './RegistrationSection';
import SubmitDealershipFormButton from '../components/SubmitDealershipFormButton';
import initialMetricsPublisher from '../metrics';

import {DEALERSHIP_INFORMATION, CONTRACTS, AAC_REGISTER, OVERVIEW, getStepById, STEP_STATE} from 'src/constants/Steps';
import WelcomePage from './WelcomePage';
import FAQ from '../components/FAQ';
import {
    US_STATES,
    PARENT_COMPANIES,
    DMS,
    CRM,
    CREDIT_SUBMISSION,
    INVENTORY_SYNDICATION,
    OEM_BRANDS,
    DEFAULT_FORM,
    DEFAULT_FORM_ERRORS,
    BD_OWNER_OPTIONS, NOT_PROVIDED, INDEPENDENT_DEALER
} from 'src/constants/DealershipInfoConstants';
import {ERROR_MESSAGES} from 'src/constants/ErrorMessages';
import {DealershipInfo} from 'src/model/DealershipInfo';
import {FormErrors} from 'src/model/FormErrors';
import {
    createInputChangeHandler,
    createPhoneChangeHandler,
    createCheckboxChangeHandler,
    createZipCodeChangeHandler
} from 'src/modules/FormChangeHandlers';

import {
    createPhoneBlurHandler,
    createStateBlurHandler,
    createZipCodeBlurHandler,
    createEmailBlurHandler,
    createContactFieldBlurHandler,
    createBusinessFieldBlurHandler,
    validateRequiredFields,
    isFormValid, createSoftwareFieldBlurHandler
} from 'src/modules/FormValidators';
import {transformDmsValue, transformLeasingValue, transformPhoneForApi} from 'src/modules/FormDataTransformers';
import {validateIntake} from 'src/backend/ValidateIntakeCaller';
import '../assets/dealershipForm.scss';
import '../assets/welcome.scss';
import {CONTACT_US_URL, NEW_TAB} from 'src/constants/Links';
import {createA2ZQuote} from "src/backend/CreateA2ZQuoteCaller";
import {submitIntake, SUCCESS} from "src/backend/SubmitIntakeCaller";
import {notifyNewIntake} from "src/backend/NotifyNewIntakeCaller";
import {generateSafeguardUrl} from "src/modules/SafeguardUrlGenerator";
import user_icon from 'src/images/icon/user_icon.png'
import house_icon from 'src/images/icon/house_icon.png'
import wallet_icon from 'src/images/icon/wallet_icon.png'
import mobile_icon from 'src/images/icon/mobile_icon.png'
import business_icon from 'src/images/icon/business_icon.png'
import {getDealerSelectionData} from "src/backend/GetDealerSelectionDataCaller";
import FormFieldSingleAutocompleteDropdown from "src/components/FormFieldSingleAutocompleteDropdown";

const DealershipForm: React.FC = () => {
    const [currentStep, setCurrentStep] = useState(STEP_STATE[OVERVIEW]);
    const [showFAQ, setShowFAQ] = useState(false);
    const [hasInteractedWithForm, setHasInteractedWithForm] = useState(false);
    const [pageOpenTime] = useState(Date.now());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [a2zQuoteUrl, setA2zQuoteUrl] = useState<string>('');
    const [safeguardContractUrl, setSafeguardContractUrl] = useState<string>('');
    const [a2zAssociationId, setA2zAssociationId] = useState<string>('');
    const [submitIntakeSuccess, setSubmitIntakeSuccess] = useState(false);
    const [sameContactToggle, setSameContactToggle] = useState(false);
    const [intakeId, setIntakeId] = useState<string>('');

    const [formData, setFormData] = useState<DealershipInfo>(DEFAULT_FORM);
    const [errors, setErrors] = useState<FormErrors>(DEFAULT_FORM_ERRORS);
    const [dealerSelections, setDealerSelections] = useState([] as string[]);
    const [dealerGroupSelections, setDealerGroupSelections] = useState([] as string[]);

    useEffect(() => {
        // Define the asynchronous function
        const fetchData = async () => {
            // Use await with your API call (e.g., using axios or fetch)
            const response = await getDealerSelectionData({});
            setDealerSelections(response.dealers);
            setDealerGroupSelections(response.dealerGroups);
        };

        fetchData();
    }, []);

    const trackInteraction = () => {
        if (!hasInteractedWithForm) {
            const actionPublisher = initialMetricsPublisher.newChildActionPublisherForMethod('DealershipFormInteraction');
            actionPublisher.publishCounterMonitor('InteractedWithDealershipFormCount', 1);
            setHasInteractedWithForm(true);
        }
    };
    
    const createTrackedInputHandler = (errorField: keyof FormErrors) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        trackInteraction();
        createInputChangeHandler(setFormData, setErrors, errorField)(e);
    };

    const handleContactPhoneChange = createPhoneChangeHandler(setFormData, setErrors, 'phoneError');
    const handleBusinessPhoneChange = createPhoneChangeHandler(setFormData, setErrors, 'businessPhoneError');
    const handleZipCodeChange = createZipCodeChangeHandler(setFormData, setErrors, 'zipCodeError');
    const handleContactPhoneBlur = createPhoneBlurHandler(setErrors, 'phoneError');
    const handleBusinessPhoneBlur = createPhoneBlurHandler(setErrors, 'businessPhoneError');
    const handleStateBlur = createStateBlurHandler(setFormData, setErrors, 'stateError');
    const handleZipCodeBlur = createZipCodeBlurHandler(setErrors, 'zipCodeError');
    const handleContactEmailBlur = createEmailBlurHandler(setErrors, 'emailError');
    const handleBusinessEmailBlur = createEmailBlurHandler(setErrors, 'businessEmailError');
    const handleRegistrationEmailBlur = createEmailBlurHandler(setErrors, 'registrationEmailError');
    const handleVehicleConditionsChange = createCheckboxChangeHandler(setFormData, setErrors, 'vehicleConditionsError');
    const handleContactFieldBlur = (fieldName: string, errorField: keyof FormErrors) => () => {
        createContactFieldBlurHandler(setErrors, fieldName, errorField)(formData);
    };
    const handleBusinessFieldBlur = (fieldName: string, errorField: keyof FormErrors) => () => {
        createBusinessFieldBlurHandler(setErrors, fieldName, errorField)(formData);
    };
    const handleSoftwareFieldBlur = (fieldName: string, errorField: keyof FormErrors) => () => {
        createSoftwareFieldBlurHandler(setErrors, fieldName, errorField)(formData);
    };
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (validateRequiredFields(formData, setErrors)) {
            setIsSubmitting(true);

            // If user selected Not provided, use their user input instead.
            formData.business.name = formData.business.name === NOT_PROVIDED ?
                formData.business.userInputName! : formData.business.name;

            // If user selected Not provided, use their user input instead.
            formData.business.groupName = formData.business.groupName === NOT_PROVIDED ?
                formData.business.userInputGroupName! : formData.business.groupName;

            // If user selected Independent Dealer, blank out the group name field as there really is no group name.
            formData.business.groupName = formData.business.groupName === INDEPENDENT_DEALER ?
                '' : formData.business.groupName

            try {
                const actionPublisher = initialMetricsPublisher.newChildActionPublisherForMethod('FormSubmissionTime');
                actionPublisher.publishTimer('PageOpenToSubmittedFormSuccessfullyTime', Date.now() - pageOpenTime);

                const intakeId = crypto.randomUUID();
                setIntakeId(intakeId);

                // Call ValidateIntake
                const validateIntakeResponse = await validateIntake({
                    intakeId: intakeId,
                    businessName: formData.business.name,
                    zipCode: formData.business.zipCode
                });
                const reportingId = validateIntakeResponse.reportingId;

                // Call NotifyNewIntake
                const notifyNewIntakeResponse = await notifyNewIntake({
                    transaction_id: intakeId,
                    ...(reportingId && { reporting_id: reportingId })
                });

                // Call CreateA2ZQuote
                const createA2ZQuoteResponse = await createA2ZQuote({
                    intakeId: intakeId,
                    ...(reportingId && { reportingId }),
                    contact: {
                        ...formData.contact,
                        phone: transformPhoneForApi(formData.contact.phone),
                        registrationContactFirstName: sameContactToggle ? formData.contact.firstName : formData.contact.registrationContactFirstName,
                        registrationContactLastName: sameContactToggle ? formData.contact.lastName : formData.contact.registrationContactLastName,
                        registrationContactEmail: sameContactToggle ? formData.contact.email : formData.contact.registrationContactEmail
                    },
                    business: {
                        ...(() => {
                            const { brands, websiteLink, privacyPolicyLink, oemWarrantyOnly, ...rest } = formData.business;
                            return {
                                ...rest,
                                phone: transformPhoneForApi(formData.business.phone),
                                leasing: transformLeasingValue(formData.business.leasing),
                                ...(brands && brands.length > 0 && { brands }),
                                ...(websiteLink && { websiteLink }),
                                ...(privacyPolicyLink && { privacyPolicyLink })
                            };
                        })()
                    },
                    software: { ...formData.software, dmsProvider: transformDmsValue(formData.software.dmsProvider)}
                })
                // Sets the A2ZQuoteURL and SafeguardContractURL to be rendered on the contract page
                setA2zQuoteUrl(createA2ZQuoteResponse.quoteUrl);
                setA2zAssociationId(createA2ZQuoteResponse.a2zAssociationId);

                if (!formData.business.oemWarrantyOnly) {
                    setSafeguardContractUrl(generateSafeguardUrl(intakeId, formData.business));
                }

                const a2zAssociationId = createA2ZQuoteResponse.a2zAssociationId;

                // Call SubmitIntake
                const submitIntakeResponse = await submitIntake({
                    eventType: 'initial',
                    event: {
                        intakeId: intakeId,
                        ...(reportingId && { reportingId }),
                        intakeStatus: 'NEW',
                        intakeConsentGranted: true,
                        contact: {
                            ...formData.contact,
                            phone: transformPhoneForApi(formData.contact.phone),
                            registrationContactFirstName: sameContactToggle ? formData.contact.firstName : formData.contact.registrationContactFirstName,
                            registrationContactLastName: sameContactToggle ? formData.contact.lastName : formData.contact.registrationContactLastName,
                            registrationContactEmail: sameContactToggle ? formData.contact.email : formData.contact.registrationContactEmail
                        },
                        business: {
                            ...formData.business,
                            phone: transformPhoneForApi(formData.business.phone),
                            leasing: transformLeasingValue(formData.business.leasing)
                        },
                        software: { ...formData.software, dmsProvider: transformDmsValue(formData.software.dmsProvider)},
                        a2zAssociationId: a2zAssociationId,
                        bdOwner: formData.bdOwner
                    },
                    id: intakeId
                })
                setSubmitIntakeSuccess(submitIntakeResponse.outcome === SUCCESS);
            } finally {
                setIsSubmitting(false);
                setCurrentStep(STEP_STATE[CONTRACTS]);
            }
        }
    };

    if (showFAQ) {
        return <FAQ onBack={() => setShowFAQ(false)}/>;
    }

    return (
        <div className="dealership-page">
            <Header/>
            <div className="dealership-container">
                <div className="left-section">
                    <Navigation currentStep={getStepById(currentStep)} onStepChange={setCurrentStep}
                                onFAQClick={() => setShowFAQ(true)}
                                onContactUsClick={() => window.open(CONTACT_US_URL, NEW_TAB)}/>
                </div>

                <div className="right-section">
                    <div className="content-section">
                        <div className="main-content-box">
                            <div className="dealership-content">
                                {currentStep === STEP_STATE[OVERVIEW] && (
                                    <WelcomePage
                                        onGetStarted={() => setCurrentStep(STEP_STATE[DEALERSHIP_INFORMATION])}/>
                                )}
                                {currentStep === STEP_STATE[DEALERSHIP_INFORMATION] && (
                                    <>
                                        <h1 className="page-heading">1. Dealership information</h1>
                                        <p className="page-description">In this step, we're collecting information about
                                            your dealership to be used for creating your business on Amazon Autos.</p>
                                        <p className="page-description">*Required section</p>

                                        <form onSubmit={handleSubmit}>
                                            <FormSection title="Contact information*">
                                                <div className="form-row">
                                                    <FormField
                                                        label="First name"
                                                        name="contact.firstName"
                                                        value={formData.contact.firstName}
                                                        onChange={createTrackedInputHandler('firstNameError')}
                                                        onBlur={handleContactFieldBlur('firstName', 'firstNameError')}
                                                        placeholder="John"
                                                        error={errors.firstNameError}
                                                        errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                    />
                                                    <FormField
                                                        label="Last name"
                                                        name="contact.lastName"
                                                        value={formData.contact.lastName}
                                                        onChange={createTrackedInputHandler('lastNameError')}
                                                        onBlur={handleContactFieldBlur('lastName', 'lastNameError')}
                                                        placeholder="Doe"
                                                        error={errors.lastNameError}
                                                        errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                    />
                                                </div>
                                                <div className="form-row">
                                                    <FormField
                                                        label="Title"
                                                        name="contact.title"
                                                        value={formData.contact.title}
                                                        onChange={createTrackedInputHandler('titleError')}
                                                        onBlur={handleContactFieldBlur('title', 'titleError')}
                                                        infoMessage="Your position or role at the dealership. Don't enter any personal information here such as SSN, date of birth, or passwords."
                                                        placeholder="General Manager"
                                                        error={errors.titleError}
                                                        errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                    />
                                                    <FormField
                                                        label="Phone number"
                                                        name="contact.phone"
                                                        type="tel"
                                                        value={formData.contact.phone}
                                                        onChange={handleContactPhoneChange}
                                                        onBlur={handleContactPhoneBlur}
                                                        placeholder="555-555-5555"
                                                        error={errors.phoneError}
                                                        errorMessage={formData.contact.phone.trim() === '' ? ERROR_MESSAGES.REQUIRED_FIELD : ERROR_MESSAGES.INVALID_PHONE}
                                                    />
                                                </div>
                                                <EmailVerificationField
                                                    label="Email"
                                                    name="contact.email"
                                                    value={formData.contact.email}
                                                    onChange={createTrackedInputHandler('emailError')}
                                                    onBlur={handleContactEmailBlur}
                                                    setFormData={setFormData}
                                                    emailError={errors.emailError}
                                                    emailVerified={formData.emailVerified}
                                                    consentConfirmed={formData.consentConfirmed}
                                                    infoMessage="Do not use a personal email address. Use a company email address"
                                                    placeholder="johndoe@email.com"
                                                />
                                            </FormSection>
                                            <FormSection 
                                                title="Account setup contact information"
                                                infoMessage="The person who will handle your Seller Central registration and software setup with Amazon."
                                                toggleText={"Same as above"}
                                                toggleChecked={sameContactToggle}
                                                setToggleChecked={setSameContactToggle}
                                            >
                                                {!sameContactToggle && 
                                                    <div>
                                                        <div className="form-row">
                                                            <FormField
                                                                label="First name"
                                                                name="contact.registrationContactFirstName"
                                                                value={formData.contact.registrationContactFirstName}
                                                                onChange={createTrackedInputHandler('registrationFirstNameError')}
                                                                onBlur={handleContactFieldBlur('registrationContactFirstName', 'registrationFirstNameError')}
                                                                placeholder="John"
                                                                error={errors.registrationFirstNameError}
                                                                errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                            />
                                                            <FormField
                                                                label="Last name"
                                                                name="contact.registrationContactLastName"
                                                                value={formData.contact.registrationContactLastName}
                                                                onChange={createTrackedInputHandler('registrationLastNameError')}
                                                                onBlur={handleContactFieldBlur('registrationContactLastName', 'registrationLastNameError')}
                                                                placeholder="Doe"
                                                                error={errors.registrationLastNameError}
                                                                errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                            />
                                                        </div>
                                                        <FormField
                                                            label="Email"
                                                            name="contact.registrationContactEmail"
                                                            type="email"
                                                            value={formData.contact.registrationContactEmail}
                                                            onChange={createTrackedInputHandler('registrationEmailError')}
                                                            onBlur={handleRegistrationEmailBlur}
                                                            placeholder="johndoe@email.com"
                                                            className="form-field-full"
                                                            infoMessage="Don't enter any personal information here such as SSN, date of birth, or passwords."
                                                            error={errors.registrationEmailError}
                                                            errorMessage={formData.contact.registrationContactEmail.trim() === '' ? ERROR_MESSAGES.REQUIRED_FIELD : ERROR_MESSAGES.INVALID_EMAIL}
                                                        />
                                                    </div>
                                                }
                                            </FormSection>
                                            <FormSection title="Business information*">
                                                <div className="form-row">
                                                    <FormFieldSingleAutocompleteDropdown
                                                        label="Dealer Group Name"
                                                        name="business.groupName"
                                                        value={formData.business.groupName}
                                                        onChange={(selectedGroupName) => {
                                                            trackInteraction();
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                business: { ...prev.business, groupName: selectedGroupName }
                                                            }));
                                                            setErrors(prev => ({ ...prev, businessGroupNameError: false }));
                                                        }}
                                                        options={dealerGroupSelections}
                                                        className="form-field"
                                                        placeholder="ABC Dealer Group"
                                                        error={errors.businessGroupNameError}
                                                        errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                        notProvidedExplanation="If not provided, enter the dealer group name below."
                                                        showNotProvidedExplanation={formData.business.groupName === NOT_PROVIDED}
                                                    />
                                                    <FormFieldSingleAutocompleteDropdown
                                                        label="Dealer Store Name*"
                                                        name="business.name"
                                                        value={formData.business.name}
                                                        onChange={(selectedName) => {
                                                            trackInteraction();
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                business: { ...prev.business, name: selectedName }
                                                            }));
                                                            setErrors(prev => ({ ...prev, businessNameError: false }));
                                                        }}
                                                        options={dealerSelections}
                                                        className="form-field"
                                                        placeholder="ABC Dealership"
                                                        error={errors.businessNameError}
                                                        errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                        notProvidedExplanation="If not provided, enter the dealer store name below."
                                                        showNotProvidedExplanation={formData.business.name === NOT_PROVIDED}
                                                    />
                                                </div>
                                                {(formData.business.name === NOT_PROVIDED || formData.business.groupName === NOT_PROVIDED) && <div className="form-row">
                                                    <div className={`form-field-with-cushion-row${formData.business.groupName === NOT_PROVIDED ? '' : ' hidden'}`}>
                                                        <div className="field-cushion"></div>
                                                        <FormField
                                                            name="business.userInputGroupName"
                                                            value={formData.business.userInputGroupName}
                                                            onChange={createTrackedInputHandler('userInputBusinessGroupNameError')}
                                                            error={errors.userInputBusinessGroupNameError}
                                                            errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                        />
                                                    </div>
                                                    <div className={`form-field-with-cushion-row${formData.business.name === NOT_PROVIDED ? '' : ' hidden'}`}>
                                                        <div className="field-cushion"></div>
                                                        <FormField
                                                            name="business.userInputName"
                                                            value={formData.business.userInputName}
                                                            onChange={createTrackedInputHandler('userInputBusinessNameError')}
                                                            error={errors.userInputBusinessNameError}
                                                            errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                        />
                                                    </div>
                                                </div>}
                                                <FormField
                                                    label="Street address"
                                                    name="business.streetAddress"
                                                    value={formData.business.streetAddress}
                                                    onChange={createTrackedInputHandler('streetAddressError')}
                                                    onBlur={handleBusinessFieldBlur('streetAddress', 'streetAddressError')}
                                                    className="form-field-full"
                                                    infoMessage="Don't enter any personal information here such as SSN, date of birth, or passwords."
                                                    placeholder="555 Dealership Ln."
                                                    error={errors.streetAddressError}
                                                    errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                />
                                                <div className="form-row">
                                                    <FormField
                                                        label="City"
                                                        name="business.city"
                                                        value={formData.business.city}
                                                        onChange={createTrackedInputHandler('cityError')}
                                                        onBlur={handleBusinessFieldBlur('city', 'cityError')}
                                                        className="form-field-city"
                                                        infoMessage="Don't enter any personal information here such as SSN, date of birth, or passwords."
                                                        placeholder="Philadelphia"
                                                        error={errors.cityError}
                                                        errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                    />
                                                    <div className="form-field">
                                                        <label className="form-label">State</label>
                                                        <input
                                                            type="text"
                                                            name="business.state"
                                                            value={formData.business.state}
                                                            onChange={createTrackedInputHandler('stateError')}
                                                            onBlur={handleStateBlur}
                                                            className={`form-input ${errors.stateError ? 'form-input-error' : ''}`}
                                                            placeholder="PA"
                                                            list="states"
                                                        />
                                                        {errors.stateError && <div
                                                            className="field-error">{formData.business.state.trim() === '' ? ERROR_MESSAGES.REQUIRED_FIELD : ERROR_MESSAGES.INVALID_STATE}</div>}
                                                        <datalist id="states">
                                                            {US_STATES.map(state => <option key={state}
                                                                                            value={state}/>)}
                                                        </datalist>
                                                    </div>
                                                    <div className="form-field form-field-zipcode">
                                                        <label className="form-label">ZIP Code</label>
                                                        <input
                                                            type="text"
                                                            name="business.zipCode"
                                                            value={formData.business.zipCode}
                                                            onChange={handleZipCodeChange}
                                                            onBlur={handleZipCodeBlur}
                                                            className={`form-input ${errors.zipCodeError ? 'form-input-error' : ''}`}
                                                            placeholder="19001"
                                                        />
                                                        {errors.zipCodeError && <div
                                                            className="field-error">{formData.business.zipCode.trim() === '' ? ERROR_MESSAGES.REQUIRED_FIELD : ERROR_MESSAGES.INVALID_ZIP_CODE}</div>}
                                                    </div>
                                                </div>
                                                <FormField
                                                    label="Business email"
                                                    name="business.email"
                                                    type="email"
                                                    value={formData.business.email}
                                                    onChange={createTrackedInputHandler('businessEmailError')}
                                                    onBlur={handleBusinessEmailBlur}
                                                    placeholder="johndoe@dealership.com"
                                                    className="form-field-full"
                                                    infoMessage="Don't enter any personal information here such as SSN, date of birth, or passwords."
                                                    error={errors.businessEmailError}
                                                    errorMessage={formData.business.email.trim() === '' ? ERROR_MESSAGES.REQUIRED_FIELD : ERROR_MESSAGES.INVALID_EMAIL}
                                                />
                                                <FormField
                                                    label="Business phone number"
                                                    name="business.phone"
                                                    type="tel"
                                                    value={formData.business.phone}
                                                    onChange={handleBusinessPhoneChange}
                                                    onBlur={handleBusinessPhoneBlur}
                                                    placeholder="555-555-5555"
                                                    className="form-field-full"
                                                    infoMessage="Don't enter any personal information here such as SSN, date of birth, or passwords."
                                                    error={errors.businessPhoneError}
                                                    errorMessage={formData.business.phone.trim() === '' ? ERROR_MESSAGES.REQUIRED_FIELD : ERROR_MESSAGES.INVALID_PHONE}
                                                />
                                                <FormField
                                                    label="Business privacy policy link"
                                                    name="business.privacyPolicyLink"
                                                    type="url"
                                                    value={formData.business.privacyPolicyLink}
                                                    onChange={createTrackedInputHandler('businessPrivacyPolicyError')}
                                                    className="form-field-full"
                                                    infoMessage="Don't enter any personal information here such as SSN, date of birth, or passwords."
                                                    placeholder="https://"
                                                    showClearButton={true}
                                                    onClear={() => setFormData(prev => ({
                                                        ...prev,
                                                        business: {...prev.business, privacyPolicyLink: ''}
                                                    }))}
                                                />
                                                <FormField
                                                    label="Business website link"
                                                    name="business.websiteLink"
                                                    type="url"
                                                    value={formData.business.websiteLink}
                                                    onChange={createTrackedInputHandler('businessWebsiteError')}
                                                    className="form-field-full"
                                                    placeholder="https://"
                                                    infoMessage="Don't enter any personal information here such as SSN, date of birth, or passwords."
                                                    showClearButton={true}
                                                    onClear={() => setFormData(prev => ({
                                                        ...prev,
                                                        business: {...prev.business, websiteLink: ''}
                                                    }))}
                                                />
                                                <FormField
                                                    label="State dealer license number"
                                                    name="business.dealerLicenseNumber"
                                                    value={formData.business.dealerLicenseNumber}
                                                    onChange={createTrackedInputHandler('dealerLicenseError')}
                                                    onBlur={handleBusinessFieldBlur('dealerLicenseNumber', 'dealerLicenseError')}
                                                    className="form-field-full"
                                                    infoMessage="Your state business license number, provided by DMV - used to verify your business. Don't enter any personal information here such as SSN, date of birth, or passwords."
                                                    error={errors.dealerLicenseError}
                                                    errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                />
                                                <FormField
                                                    label="OEM dealer ID"
                                                    name="business.oemDealerId"
                                                    value={formData.business.oemDealerId}
                                                    onChange={createTrackedInputHandler('oemDealerIdError')}
                                                    onBlur={handleBusinessFieldBlur('oemDealerId', 'oemDealerIdError')}
                                                    className="form-field-full"
                                                    infoMessage="Franchise dealer ID provided by OEM. Don't enter any personal information here such as SSN, date of birth, or passwords."
                                                    error={errors.oemDealerIdError}
                                                    errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                />
                                                <FormFieldMultiSelectDropdown
                                                    label="OEM/brand"
                                                    name="business.brands"
                                                    value={formData.business.brands}
                                                    onChange={(selectedBrands) => {
                                                        trackInteraction();
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            business: {
                                                                ...prev.business,
                                                                brands: selectedBrands
                                                            }
                                                        }));
                                                        setErrors(prev => ({ ...prev, oemBrandError: false }));
                                                    }}
                                                    options={OEM_BRANDS}
                                                    className="form-field-full"
                                                    placeholder="Select brands"
                                                    infoMessage="The brands you are franchised with. Don't enter any personal information here such as SSN, date of birth, or passwords."
                                                    error={errors.oemBrandError}
                                                    errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                    onBlur={handleBusinessFieldBlur('brands', 'oemBrandError')}
                                                />
                                                <div className="form-field-full oem-warranty-section">
                                                    <label className="form-label">Are you planning to only offer OEM warranty products for vehicles listed on Amazon? Selecting "Yes" allows you to skip enrollment for Safe-Guard Extra Protect.</label>
                                                    <div className="checkbox-row">
                                                        {['Yes', 'No'].map(option => (
                                                            <label key={option} className="radiobutton-label">
                                                                <input
                                                                    type="radio"
                                                                    name="business.oemWarrantyOnly"
                                                                    value={option}
                                                                    checked={formData.business.oemWarrantyOnly === (option === 'Yes')}
                                                                    onChange={() => {
                                                                        trackInteraction();
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            business: { ...prev.business, oemWarrantyOnly: option === 'Yes' }
                                                                        }));
                                                                        setErrors(prev => ({ ...prev, oemWarrantyOnlyError: false }));
                                                                    }}
                                                                />
                                                                <span>{option}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                    {errors.oemWarrantyOnlyError &&
                                                        <div className="field-error">{ERROR_MESSAGES.REQUIRED_FIELD}</div>}
                                                </div>
                                                <div className="form-field-full vehicle-types-section vehicle-types-spacing">
                                                    <label className="form-label">What types of vehicles are you interested in
                                                        selling on Amazon Autos? (Select all that apply)</label>
                                                    <div className="checkbox-row">
                                                        {['New', 'Used', 'Certified pre-owned'].map(condition => (
                                                            <label key={condition} className="vehicle-checkbox-label">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={formData.business.vehicleConditions.includes(condition)}
                                                                    onChange={() => handleVehicleConditionsChange('business.vehicleConditions', condition)}
                                                                />
                                                                <span>{condition}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                    {errors.vehicleConditionsError &&
                                                        <div className="field-error">{ERROR_MESSAGES.REQUIRED_FIELD}</div>}
                                                </div>
                                                <div className="form-field-full leasing-interest-section">
                                                    <label className="form-label">Are you interested in leasing vehicles on
                                                        Amazon Autos?</label>
                                                    <div className="checkbox-row">
                                                        {['Yes', 'No', 'Undecided, need more information'].map(option => (
                                                            <label key={option} className="radiobutton-label">
                                                                <input
                                                                    type="radio"
                                                                    name="business.leasing"
                                                                    value={option}
                                                                    checked={formData.business.leasing === option}
                                                                    onChange={(e) => createTrackedInputHandler('leasingError')(e)}
                                                                />
                                                                <span>{option}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                    {errors.leasingError &&
                                                        <div className="field-error">{ERROR_MESSAGES.REQUIRED_FIELD}</div>}
                                                </div>
                                                <div className="form-field-full parent-company-section">
                                                    <label className="form-label">What parent company are you part of?*</label>
                                                    <div className="checkbox-row">
                                                        {PARENT_COMPANIES.map(option => (
                                                            <label key={option} className="radiobutton-label">
                                                                <input
                                                                    type="radio"
                                                                    name="business.parentCompany"
                                                                    value={option}
                                                                    checked={formData.business.parentCompany === option}
                                                                    onChange={(e) => createTrackedInputHandler('parentCompanyError')(e)}
                                                                />
                                                                <span>{option}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                    {errors.parentCompanyError &&
                                                        <div className="field-error">{ERROR_MESSAGES.REQUIRED_FIELD}</div>}
                                                </div>
                                                <FormFieldSingleSelectDropdown
                                                    label="Have you had any contact with an Amazon Autos Business Development Manager?*"
                                                    name="bdOwner"
                                                    value={formData.bdOwner}
                                                    onChange={(selectedBd) => {
                                                        trackInteraction();
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            bdOwner: selectedBd
                                                        }));
                                                        setErrors(prev => ({ ...prev, bdOwnerError: false }));
                                                    }}
                                                    options={BD_OWNER_OPTIONS}
                                                    className="form-field-full"
                                                    placeholder="Select a Business Development Manager"
                                                    infoMessage="The person who may have informed you about selling on Amazon Autos."
                                                    error={errors.bdOwnerError}
                                                    errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                />
                                                <FormFieldSingleSelectDropdown
                                                    label="Which dealer management system (DMS) do you use?*"
                                                    name="software.dmsProvider"
                                                    value={formData.software.dmsProvider}
                                                    onChange={(selectedDms) => {
                                                        trackInteraction();
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            software: { ...prev.software, dmsProvider: selectedDms }
                                                        }));
                                                        setErrors(prev => ({ ...prev, dmsProviderError: false }));
                                                    }}
                                                    options={DMS}
                                                    className="form-field-full"
                                                    placeholder="Select a DMS"
                                                    infoMessage="Used for A2Z Sync addendums."
                                                    error={errors.dmsProviderError}
                                                    errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                />
                                                <FormField
                                                    label="If enrolled with Dealertrack for lending, please provide your Dealertrack ID number:"
                                                    name="software.dealertrackId"
                                                    value={formData.software.dealertrackId}
                                                    onChange={createTrackedInputHandler('dealertrackIdError')}
                                                    onBlur={handleSoftwareFieldBlur('dealertrackId', 'dealertrackIdError')}
                                                    className="form-field-full"
                                                    infoMessage="Don't enter any personal information here such as SSN, date of birth, or passwords."
                                                    error={errors.oemDealerIdError}
                                                    errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                />
                                                <FormFieldSingleSelectDropdown
                                                    label="Which customer relationship management software (CRM) do you use?*"
                                                    name="software.crmProvider"
                                                    value={formData.software.crmProvider}
                                                    onChange={(selectedCrm) => {
                                                        trackInteraction();
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            software: { ...prev.software, crmProvider: selectedCrm }
                                                        }));
                                                        setErrors(prev => ({ ...prev, crmProviderError: false }));
                                                    }}
                                                    options={CRM}
                                                    className="form-field-full"
                                                    placeholder="Select a CRM"
                                                    error={errors.crmProviderError}
                                                    errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                />
                                                <FormFieldSingleSelectDropdown
                                                    label="Which credit submission provider do you use?*"
                                                    name="software.creditSubmissionProvider"
                                                    value={formData.software.creditSubmissionProvider}
                                                    onChange={(selectedProvider) => {
                                                        trackInteraction();
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            software: { ...prev.software, creditSubmissionProvider: selectedProvider }
                                                        }));
                                                        setErrors(prev => ({ ...prev, creditSubmissionProviderError: false }));
                                                    }}
                                                    options={CREDIT_SUBMISSION}
                                                    className="form-field-full"
                                                    placeholder="Select a credit submission provider"
                                                    error={errors.creditSubmissionProviderError}
                                                    errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                />
                                                <FormFieldSingleSelectDropdown
                                                    label="Which inventory syndication provider do you use?*"
                                                    name="software.imsProvider"
                                                    value={formData.software.imsProvider}
                                                    onChange={(selectedProvider) => {
                                                        trackInteraction();
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            software: { ...prev.software, imsProvider: selectedProvider }
                                                        }));
                                                        setErrors(prev => ({ ...prev, imsProviderError: false }));
                                                    }}
                                                    options={INVENTORY_SYNDICATION}
                                                    className="form-field-full form-field-last"
                                                    placeholder="Select an inventory syndication provider"
                                                    error={errors.imsProviderError}
                                                    errorMessage={ERROR_MESSAGES.REQUIRED_FIELD}
                                                />
                                            </FormSection>
                                        </form>


                                        <div className="consent-section">
                                            <SubmitDealershipFormButton
                                                consentConfirmed={formData.consentConfirmed}
                                                onConsentChange={(checked) => {
                                                    if (checked) {
                                                        validateRequiredFields(formData, setErrors);
                                                    }
                                                    setFormData(prev => ({...prev, consentConfirmed: checked}));
                                                }}
                                                onSubmit={handleSubmit}
                                                isFormValid={isFormValid(formData, errors) && formData.emailVerified}
                                                isSubmitting={isSubmitting}
                                                consentMessage="I confirm the information provided is accurate and consent to share the information with Amazon Autos' third-party providers for the purposes of streamlining onboarding to Amazon Autos."
                                                warningMessage="Please note that upon submission of this form, you won't be able to go back to this page to make further modifications."
                                                buttonText="Submit information"
                                                recordMetric={true}
                                            />
                                        </div>
                                    </>
                                )}
                                {currentStep === STEP_STATE[CONTRACTS] && (
                                    <ContractsSection a2zQuoteUrl={a2zQuoteUrl} safeguardContractUrl={safeguardContractUrl} submitIntakeSuccess={submitIntakeSuccess} a2zAssociationId={a2zAssociationId} skipSafeguard={formData.business.oemWarrantyOnly} intakeId={intakeId} onSubmit={() => setCurrentStep(STEP_STATE[AAC_REGISTER])}/>
                                )}
                                {currentStep === STEP_STATE[AAC_REGISTER] && (
                                    <RegistrationSection/>
                                )}
                            </div>
                        </div>

                        {currentStep === STEP_STATE[AAC_REGISTER] && (
                            <div className="sidebar-box">
                                <div className="sidebar-inner">
                                    <div className="sidebar-heading">
                                        Amazon Seller Central registration will need:
                                    </div>
                                    <div className="sidebar-points">
                                        <div className="requirement">
                                            <img className="user-icon" src={business_icon} alt=""/>
                                            <span>Business information</span>
                                        </div>
                                        <div className="requirement">
                                            <img className="user-icon" src={user_icon} alt=""/>
                                            <span>Valid government-issued ID</span>
                                        </div>
                                        <div className="requirement">
                                            <img className="house-icon" src={house_icon} alt=""/>
                                            <span>Recent bank account or credit card statement</span>
                                        </div>
                                        <div className="requirement">
                                            <img className="wallet-icon" src={wallet_icon} alt=""/>
                                            <span>Credit card</span>
                                        </div>
                                        <div className="requirement">
                                            <img className="mobile-icon" src={mobile_icon} alt=""/>
                                            <span>Mobile phone</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {currentStep !== STEP_STATE[OVERVIEW] && currentStep !== STEP_STATE[AAC_REGISTER] && currentStep !== STEP_STATE[CONTRACTS] && <Sidebar/>}
                    </div>
                </div>
            </div>
            <Footer/>
        </div>
    );
};

export default DealershipForm